import { Box } from '@mui/material';
import { styled } from '@mui/material/styles';

const TableWrapper = styled(Box)(({ theme }) => {
  const isDark = theme.palette.mode === 'dark';
  return {
    position: 'relative',
    width: '100%',
    flex: 1,
    minHeight: 0,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    overscrollBehaviorX: 'none',

    /* ── Vertical scroll container ───────────────────────── */
    '& .oc-outer': {
      flex: 1,
      minHeight: 0,
      overflowY: 'auto',
      overflowX: 'hidden',
      width: '100%',
      WebkitOverflowScrolling: 'touch',
      overscrollBehaviorY: 'contain',
      maxHeight: '100%',
      height: '100%',
      scrollbarWidth: 'auto',
      '&::-webkit-scrollbar': {
        width: '10px',
        display: 'block'
      },
      '&::-webkit-scrollbar-track': {
        backgroundColor: isDark ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.05)',
        borderRadius: '5px'
      },
      '&::-webkit-scrollbar-thumb': {
        backgroundColor: isDark ? 'rgba(255, 255, 255, 0.35)' : 'rgba(0, 0, 0, 0.35)',
        borderRadius: '5px',
        border: `2px solid ${theme.palette.background.paper}`,
        backgroundClip: 'padding-box',
        '&:hover': {
          backgroundColor: isDark ? 'rgba(255, 255, 255, 0.55)' : 'rgba(0, 0, 0, 0.55)'
        }
      }
    },

    /* ── Header block (sits above scroll container) ──────── */
    '& .oc-header': {
      zIndex: 10,
      backgroundColor: theme.palette.background.paper,
      borderBottom: `1px solid ${theme.palette.divider}`,
      flexShrink: 0,
    },

    /* Call / Put label row */
    '& .oc-header-labels': {
      display: 'flex',
      alignItems: 'center',
      borderBottom: `1px solid ${theme.palette.divider}`
    },

    '& .oc-label--ce': {
      flex: 1
    },

    '& .oc-label--pe': {
      flex: 1
    },

    '& .oc-label--strike': {
      flex: '0 0 auto'
    },

    '& .oc-super-header': {
      padding: '4px 0',
      fontSize: '0.8rem',
      fontWeight: 600,
      color: theme.palette.text.secondary,
      textAlign: 'center',
      userSelect: 'none',
      backgroundColor: theme.palette.background.paper
    },

    /* ── Column header row (flex row: CE headers | Strike header | PE headers) ── */
    '& .oc-header-cols': {
      display: 'flex',
      alignItems: 'stretch',
    },

    /* ── CE & PE clip containers (shared for both header and body) ── */
    '& .oc-ce-clip, & .oc-pe-clip': {
      flex: 1,
      minWidth: 0,
      overflowX: 'scroll',
      overflowY: 'visible',
      scrollbarWidth: 'none',
      msOverflowStyle: 'none',
      overscrollBehaviorX: 'contain',
      '&::-webkit-scrollbar': {
        display: 'none'
      }
    },

    '& .oc-pe-clip': {
      paddingRight: '8px'
    },

    /* ── Strike column panels ───────────────────────────── */
    '& .oc-strike-head, & .oc-strike-body': {
      flex: '0 0 auto'
    },

    /* ── Body flex row ──────────────────────────────────── */
    '& .oc-body': {
      display: 'flex',
      alignItems: 'flex-start',
      position: 'relative'
    },

    /* ── Tables ─────────────────────────────────────────── */
    '& .oc-table': {
      tableLayout: 'fixed',
      borderCollapse: 'collapse',
      borderSpacing: 0,
      backgroundColor: theme.palette.background.paper
    },

    '& .oc-ce-table': {
      width: 'auto',
      marginLeft: 'auto'
    },

    '& .oc-pe-table': {
      width: 'auto',
      marginRight: 'auto'
    },

    /* ── Header cells ──────────────────────────────────── */
    '& .oc-th': {
      padding: '8px 4px',
      height: '32px',
      fontSize: '0.75rem',
      fontWeight: 600,
      color: theme.palette.text.primary,
      letterSpacing: '0.02em',
      whiteSpace: 'nowrap',
      userSelect: 'none',
      boxSizing: 'border-box',
      textAlign: 'center',
      backgroundColor: isDark ? theme.palette.grey[900] : '#f5f5f5',
      borderBottom: `2px solid ${theme.palette.divider}`
    },

    '& .oc-th--strike': {
      textAlign: 'center',
      borderBottom: `2px solid ${theme.palette.primary.main}`,
      color: theme.palette.primary.main,
      backgroundColor: isDark ? theme.palette.grey[900] : '#f5f5f5'
    },

    /* ── Body rows ─────────────────────────────────────── */
    '& .oc-row': {
      height: '32px',
      position: 'relative'
    },

    '& .oc-row--hovered .oc-td::after': {
      content: '""',
      position: 'absolute',
      inset: 0,
      backgroundColor: isDark
        ? 'rgba(255, 255, 255, 0.05)'
        : 'rgba(0, 0, 0, 0.04)',
      pointerEvents: 'none',
      zIndex: 1
    },

    /* ── Body cells ────────────────────────────────────── */
    '& .oc-td': {
      padding: 0,
      fontSize: '0.75rem',
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      boxSizing: 'border-box',
      height: '32px',
      textAlign: 'center',
      borderBottom: `1px solid ${theme.palette.divider}`,
      verticalAlign: 'middle'
    },

    '& .oc-td--strike': {
      textAlign: 'center',
      fontWeight: 700,
      fontSize: '0.8rem',
      borderLeft: `1px solid ${theme.palette.divider}`,
      borderRight: `1px solid ${theme.palette.divider}`,
      backgroundColor: isDark
        ? 'rgba(255, 255, 255, 0.02)'
        : 'rgba(0, 0, 0, 0.02)'
    },

    '& .oc-td--itm': {
      backgroundColor: isDark ? 'rgba(255, 152, 0, 0.08)' : 'rgba(255, 248, 225, 0.6)'
    },

    /* ── ATM row ───────────────────────────────────────── */
    '& .oc-atm-row': {
      height: 0,
      pointerEvents: 'none',
      position: 'relative',
      zIndex: 5
    },

    '& .oc-atm-cell': {
      padding: '0 !important',
      border: 'none !important',
      height: 0,
      position: 'relative',
      pointerEvents: 'none',
      overflow: 'visible'
    },

    '& .oc-atm-line': {
      position: 'absolute',
      left: '-9999px',
      right: '-9999px',
      top: 0,
      height: 0,
      borderTop: `1.5px dashed ${theme.palette.primary.main}`,
      pointerEvents: 'none',
      zIndex: 6
    },

    '& .oc-atm-badge': {
      position: 'absolute',
      top: 0,
      left: '50%',
      transform: 'translate(-50%, -50%)',
      zIndex: 12,
      backgroundColor: theme.palette.primary.main,
      color: theme.palette.primary.contrastText,
      borderRadius: '3px',
      padding: '2px 8px',
      fontSize: '11px',
      fontWeight: 600,
      lineHeight: 1,
      whiteSpace: 'nowrap',
      cursor: 'pointer',
      userSelect: 'none',
      pointerEvents: 'auto'
    },

    /* ── Sticky ATM badge ──────────────────────────────── */
    '& .oc-sticky-badge': {
      position: 'absolute',
      height: 0,
      zIndex: 30,
      display: 'none',
      alignItems: 'center',
      pointerEvents: 'none',
      overflow: 'visible'
    },
    '& .oc-sticky-badge::before': {
      content: '""',
      position: 'absolute',
      left: 0, right: 0, top: 0,
      height: 0,
      borderTop: `1.5px dashed ${theme.palette.primary.main}`
    },
    '& .oc-sticky-badge__inner': {
      position: 'absolute',
      top: 0,
      left: '50%',
      transform: 'translate(-50%, -50%)',
      backgroundColor: theme.palette.primary.main,
      color: theme.palette.primary.contrastText,
      borderRadius: '3px',
      padding: '2px 8px',
      fontSize: '11px',
      fontWeight: 600,
      lineHeight: 1,
      cursor: 'pointer',
      boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
      display: 'flex',
      alignItems: 'center',
      gap: '6px',
      userSelect: 'none',
      zIndex: 1,
      pointerEvents: 'auto',
      whiteSpace: 'nowrap'
    },
    '& .oc-sticky-badge__arrow': {
      fontSize: '10px'
    },

    /* ── Horizontal scrollbar ──────────────────────────── */
    '& .oc-hscroll': {
      overflowX: 'auto',
      overflowY: 'hidden',
      width: '100%',
      height: '12px',
      display: 'block',
      scrollbarWidth: 'auto',
      '&::-webkit-scrollbar': {
        height: '12px',
        display: 'block'
      },
      '&::-webkit-scrollbar-track': {
        backgroundColor: isDark ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.05)',
        borderRadius: '6px'
      },
      '&::-webkit-scrollbar-thumb': {
        backgroundColor: isDark ? 'rgba(255, 255, 255, 0.35)' : 'rgba(0, 0, 0, 0.35)',
        borderRadius: '6px',
        border: `3px solid ${theme.palette.background.paper}`,
        backgroundClip: 'padding-box',
        '&:hover': {
          backgroundColor: isDark ? 'rgba(255, 255, 255, 0.55)' : 'rgba(0, 0, 0, 0.55)'
        }
      }
    },

    '& .oc-hscroll-inner': {
      height: '1px'
    }
  };
});

export default TableWrapper;